// Vendor Libs
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {difference, filter, get, includes, last, lowerCase, map, reduce, startCase, union, without} from 'lodash';
import classnames from 'classnames';

// Components
import Checkbox from '../core/checkbox';
import Error from '../core/error';
import ElementTree from '../core/element-tree';
import Loading from '../core/loading';

// Containers
import withFormField from '../../containers/with-form-field';
import withSearch from '../../containers/with-search';

// Contexts
import {FormContext} from '../../contexts/form-context';

// Styles
import './scopes-tree.scss';

const servicesDataKey = 'scopesTree';

class ScopesTree extends Component {
    getClass() {
        const {className} = this.props;

        return classnames(
            {
                'scopes-tree': true,
            },
            className
        );
    }

    getCurrentScopes(formName) {
        const {formData, name} = this.props;

        return get(formData, [formName, 'values', name], []);
    }

    setFieldValue = (fieldName, inputScopes, {formName, setFieldValue}) => {
        const {searchData} = this.props;
        const currentScopes = this.getCurrentScopes(formName);
        let fieldValues = [...inputScopes];

        map(difference(inputScopes, currentScopes), (roleName) => {
            fieldValues = union(fieldValues, this.getChildRoles(roleName));
        });

        map(difference(currentScopes, inputScopes), (roleName) => {
            map(searchData, (role) => {
                if (includes(this.getChildRoles(role.name), roleName)) {
                    fieldValues = without(fieldValues, role.name);
                }
            });
        });

        return setFieldValue(fieldName, fieldValues);
    };

    renderName = (roleName) => {
        const {name} = this.props;
        const parts = roleName.split('_');
        const accessType = lowerCase(last(parts));

        parts.pop();

        const service = lowerCase(parts.join('_'));
        const label = startCase(
            includes(['read', 'update', 'create', 'delete'], accessType)
                ? `${accessType} ${service}`
                : accessType === 'admin'
                  ? `${service} ${accessType}`
                  : roleName
        );

        return (
            <div className="scopes-tree--wrapper">
                <Checkbox
                    className="scopes-tree--checkbox"
                    label={label}
                    name={`${name}[]`}
                    type="checkbox"
                    value={roleName}
                />
            </div>
        );
    };

    getScopesAsTree = () => {
        const {searchData} = this.props;

        return reduce(
            searchData,
            (obj, {name, childRolesLegacy}) => {
                obj[name] = childRolesLegacy;
                return obj;
            },
            {}
        );
    };

    getChildRoles = (role) => {
        const childRoles = this.getScopesAsTree();
        let children = get(childRoles, role, []);
        let flag = true;

        while (flag) {
            flag = false;

            map([...children], (childRole) => {
                if (difference(get(childRoles, childRole, []), children).length !== 0) {
                    children = union(children, get(childRoles, childRole, []));
                    flag = true;
                }
            });
        }

        return children;
    };

    getRootScopes = () => {
        const {searchData} = this.props;
        const childRoles = reduce(
            searchData,
            (names, {childRolesLegacy = []}) => {
                names.push(...childRolesLegacy);
                return names;
            },
            []
        );

        return filter(searchData, ({name}) => !includes(childRoles, name));
    };

    render() {
        const {error, fetching} = this.props;
        const childRoles = this.getScopesAsTree();

        return (
            <FormContext.Consumer>
                {(formContext) => (
                    <FormContext.Provider
                        value={{
                            ...formContext,
                            setFieldValue: (fieldName, value) => this.setFieldValue(fieldName, value, formContext),
                        }}
                    >
                        <div className={this.getClass()}>
                            <Error error={error} />
                            <Loading loading={fetching} />
                            {map(this.getRootScopes(), ({name}) => (
                                <ElementTree
                                    childElements={childRoles}
                                    elementName={name}
                                    key={name}
                                    renderName={this.renderName}
                                />
                            ))}
                        </div>
                    </FormContext.Provider>
                )}
            </FormContext.Consumer>
        );
    }
}

ScopesTree.propTypes = {
    className: PropTypes.string,
    error: PropTypes.string,
    fetching: PropTypes.bool,
    formData: PropTypes.object.isRequired,
    name: PropTypes.string,
    searchData: PropTypes.array.isRequired,
};

export default withSearch(withFormField(ScopesTree), servicesDataKey);
